import { Fragment, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "../lib/cn";
import { inviteCodeFromUrl } from "../lib/invites";
import { useUI } from "../lib/uiStore";

/**
 * Safe lightweight formatting. Text is tokenised into React nodes — never
 * injected as HTML. Supports **bold**, *italic*, _italic_, __underline__,
 * ~~strike~~, ||spoilers||, `code`, ```blocks```, "> " quotes, links,
 * @mentions, #topics and +community links. A backslash shows the next
 * markup character as-is (`\*`, `\@name`).
 */
const TOKEN =
  /(\*\*([^*\n]+)\*\*)|(~~([^~\n]+)~~)|((?<![\w*])\*([^*\s][^*\n]*?)\*(?![\w*]))|((?<!\w)_([^_\s][^_\n]*?)_(?!\w))|(https?:\/\/[^\s<]+[^\s<.,:;"')\]!?])|((?<![\w@<])@([a-zA-Z0-9_]{2,24}))|((?<![\w#&])#([a-zA-Z][a-zA-Z0-9_]{1,39}))|((?<![\w+])\+([a-z0-9][a-z0-9-]{1,30}[a-z0-9]))|((?<![\w_])__([^_\n]+)__(?![\w_]))|(\|\|([^|\n]+)\|\|)|(\*\*\*([^*\n]+)\*\*\*)|(\\([\\*_~|@#+>]))/g;

/** Hidden until clicked (or Enter/Space); stays revealed afterwards. */
function Spoiler({ children }: { children: ReactNode }) {
  const [shown, setShown] = useState(false);
  if (shown) return <span className="rounded bg-surface-2 px-0.5">{children}</span>;
  const reveal = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShown(true);
  };
  return (
    <span
      role="button"
      tabIndex={0}
      aria-label="Spoiler, select to reveal"
      onClick={reveal}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && reveal(e)}
      className="cursor-pointer select-none rounded bg-fg-3 px-0.5 text-transparent hover:bg-fg-2 [&_*]:invisible"
    >
      {children}
    </span>
  );
}

interface Opts {
  me?: string;
  linkClass: string;
  topicClass: string;
  communityClass: string;
  mentionClass: string;
  selfMentionClass: string;
}

function inline(text: string, opts: Opts, depth = 0): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(TOKEN)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const k = `${depth}-${i++}`;
    if (m[1] && depth < 3) out.push(<strong key={k} className="font-semibold">{inline(m[2], opts, depth + 1)}</strong>);
    else if (m[3] && depth < 3) out.push(<s key={k}>{inline(m[4], opts, depth + 1)}</s>);
    else if (m[5] && depth < 3) out.push(<em key={k}>{inline(m[6], opts, depth + 1)}</em>);
    else if (m[7] && depth < 3) out.push(<em key={k}>{inline(m[8], opts, depth + 1)}</em>);
    else if (m[9]) {
      const url = m[9];
      let display = url.replace(/^https?:\/\/(www\.)?/, "");
      if (display.length > 42) display = `${display.slice(0, 40)}…`;
      const invite = inviteCodeFromUrl(url);
      out.push(
        <a
          key={k}
          href={url}
          target="_blank"
          rel="noopener noreferrer nofollow ugc"
          className={`${opts.linkClass} rich-link break-all`}
          onClick={(e) => {
            e.stopPropagation();
            // Invites to this Atrium open the invite popup instead of a new tab.
            if (invite && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
              e.preventDefault();
              useUI.getState().openInvite(invite);
            }
          }}
        >
          {display}
        </a>,
      );
    } else if (m[10]) {
      const name = m[11];
      const self = opts.me && name.toLowerCase() === opts.me.toLowerCase();
      out.push(
        name.toLowerCase() === "everyone" || name.toLowerCase() === "here" ? (
          <span key={k} className={opts.mentionClass}>
            @{name}
          </span>
        ) : (
          <Link key={k} to={`/u/${name}`} className={self ? opts.selfMentionClass : opts.mentionClass} onClick={(e) => e.stopPropagation()}>
            @{name}
          </Link>
        ),
      );
    } else if (m[12]) {
      out.push(
        <Link key={k} to={`/t/${m[13].toLowerCase()}`} className={opts.topicClass} onClick={(e) => e.stopPropagation()}>
          #{m[13]}
        </Link>,
      );
    } else if (m[14]) {
      out.push(
        <Link key={k} to={`/community/${m[15]}`} className={opts.communityClass} onClick={(e) => e.stopPropagation()}>
          +{m[15]}
        </Link>,
      );
    } else if (m[16] && depth < 3) out.push(<u key={k} className="underline-offset-2">{inline(m[17], opts, depth + 1)}</u>);
    else if (m[18] && depth < 3) out.push(<Spoiler key={k}>{inline(m[19], opts, depth + 1)}</Spoiler>);
    else if (m[20] && depth < 3)
      out.push(
        <strong key={k} className="font-semibold">
          <em>{inline(m[21], opts, depth + 1)}</em>
        </strong>,
      );
    // `\*`, `\@name`…: the character itself, not formatting or a mention.
    else if (m[22]) out.push(m[23]);
    else out.push(m[0]);
    last = idx + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function withCode(text: string, opts: Opts): ReactNode[] {
  const out: ReactNode[] = [];
  const blocks = text.split(/```(?:[a-z0-9]*\n)?([\s\S]*?)```/g);
  blocks.forEach((part, bi) => {
    if (bi % 2 === 1) {
      out.push(
        <pre key={`b${bi}`} className="my-1.5 overflow-x-auto rounded-md border border-line bg-surface px-3 py-2 font-mono text-xs leading-5 text-fg">
          {part.replace(/\n$/, "")}
        </pre>,
      );
      return;
    }
    quoteGroups(part).forEach((g, gi) => {
      const nodes = inlineWithCode(g.text, opts, `${bi}-${gi}`);
      if (g.quote)
        out.push(
          <blockquote key={`q${bi}-${gi}`} className="my-0.5 border-l-[3px] border-line-strong pl-3">
            {nodes}
          </blockquote>,
        );
      else out.push(...nodes);
    });
  });
  return out;
}

function inlineWithCode(text: string, opts: Opts, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  text.split(/`([^`\n]+)`/g).forEach((seg, si) => {
    if (si % 2 === 1)
      out.push(
        <code key={`c${key}-${si}`} className="rounded bg-surface-2 px-1 py-px font-mono text-[0.9em] text-fg">
          {seg}
        </code>,
      );
    else if (seg) out.push(<Fragment key={`t${key}-${si}`}>{inline(seg, opts)}</Fragment>);
  });
  return out;
}

/**
 * Split text into runs of "> " quoted lines and everything else. The line
 * breaks around a quote are dropped because the quote is a block of its own.
 */
function quoteGroups(text: string): { quote: boolean; text: string }[] {
  if (!/(^|\n)> /.test(text)) return [{ quote: false, text }];
  const groups: { quote: boolean; lines: string[] }[] = [];
  for (const line of text.split("\n")) {
    const quote = line.startsWith("> ");
    const last = groups.at(-1);
    const body = quote ? line.slice(2) : line;
    if (last && last.quote === quote) last.lines.push(body);
    else groups.push({ quote, lines: [body] });
  }
  return groups
    .map((g) => ({ quote: g.quote, text: g.lines.join("\n") }))
    .filter((g, i, all) => g.quote || g.text !== "" || (i > 0 && i < all.length - 1));
}

export function RichText({ text, className, me, variant = "post" }: { text: string; className?: string; me?: string; variant?: "post" | "chat" }) {
  const opts: Opts = {
    me,
    linkClass: "text-accent hover:underline",
    // Topics read as quiet labels; community links as small place chips.
    topicClass: "font-medium text-fg-2 hover:text-accent",
    communityClass: "rounded-[5px] bg-surface-2 px-1 py-px font-medium text-fg hover:bg-accent-soft hover:text-accent",
    mentionClass: variant === "chat" ? "rounded px-0.5 bg-accent-soft text-accent font-medium hover:underline" : "text-accent hover:underline",
    selfMentionClass: "rounded px-0.5 bg-accent-soft text-accent font-semibold",
  };
  return <div className={cn("whitespace-pre-wrap break-words [overflow-wrap:anywhere]", className)}>{withCode(text, opts)}</div>;
}
